import React, {useState, useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts' 
import { getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'  
import { DASHBOARD_STATS } from '../../modules/auth/core/_requests' 

type Props = {
  className: string
}

interface OrderItem {
  order_item_name: string;
  total_qty: string;
  total_sale: string;
}

interface DailySales {
  order_date: string;
  total_sales: string;
}

interface Data {
  total_orders: string;
  pending_orders: string;
  completed_orders: string;
  total_customers: string;
  top_order_items: OrderItem[];
  daily_sales: DailySales[];
}

const Chart: React.FC<Props> = ({ className }) => {
  const chartRef = useRef<HTMLDivElement | null>(null) 

  const [stats, setStats] = useState<Data>({
    total_orders: "",
    pending_orders: "",
    completed_orders: "",
    total_customers: "",
    top_order_items: [],
    daily_sales: [],
  });
  const [orderDates, setOrderDates] = useState<string[]>([]);
  const [totalSales, setTotalSales] = useState<number[]>([]);
  const [loading, setLoading] = useState(false)

  const fetchStatsData = async (dashboard_stats: any) => {
    try {
      const response = await DASHBOARD_STATS(dashboard_stats);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  useEffect(() => {
    fetchStatsData("0");
  }, []);

  
  useEffect(() => {
    if (stats.daily_sales) {
      const dates = stats.daily_sales.map((item: DailySales) => item.order_date);
      const sales = stats.daily_sales.map((item: DailySales) => parseFloat(item.total_sales));
      setOrderDates(dates);
      setTotalSales(sales);
    }
  }, [stats]);

 useEffect(() => {
    if (chartRef.current && orderDates.length > 0) {
      const chart = new ApexCharts(chartRef.current, getChartOptions(totalSales, orderDates));
      chart.render();
      return () => {
        chart.destroy();
      };
    }
  }, [chartRef, orderDates, totalSales]);

  return (
    <div className={`card ${className}`}>
    <div className='card-header border-0 pt-5'>
      <h3 className='card-title align-items-start flex-column'>
        <span className='card-label fw-bold fs-3 mb-1'>Monthly Order Transactions</span>
        {totalSales.length > 0 ? (
          <span className='text-muted fw-semibold fs-7'>More than {totalSales.length} new orders</span>
        ) : (
          <span className='text-muted fw-semibold fs-7'>{totalSales.length} orders  </span>
        )}
      </h3>
    </div>

    <div className='card-body'>
    {totalSales.length > 0 ? (
      <div ref={chartRef} id='kt_charts_widget_3_chart' style={{ height: '350px' }}></div>
      ) : (
        <div className='text-muted fw-semibold fs-7 text-center pt-20' style={{ height: '350px' }}>No data available
        </div>
      )}
    </div>
  </div>
  )
}

export { Chart }

function getChartOptions(totalSales: number[], orderDates: string[]): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-info-light')

  return {
    series: [
      {
        name: 'Total Sales',
        data: totalSales,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: orderDates,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return '₹' + val + ''
        },
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  }
}
